// mfaService.js - API client for MFA features
// Handles all MFA-related API calls with authentication

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Get user's MFA status
 * @returns {Promise<object>} - { success, mfaEnabled, methods, availableMethods, enforcedByOrg }
 */
export async function getMFAStatus() {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/user-mfa-status`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get MFA status (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting MFA status:', error);
        throw error;
    }
}

/**
 * Enable MFA method
 * @param {string} method - 'email' or 'authenticator'
 * @returns {Promise<object>} - For authenticator: { success, secretKey, qrCodeUrl }
 */
export async function enableMFA(method) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/user-mfa-enable`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ method }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(errorData.error || `Failed to enable MFA (${response.status})`);
            error.statusCode = response.status;
            throw error;
        }

        return await response.json();
    } catch (error) {
        console.error('Error enabling MFA:', error);
        throw error;
    }
}

/**
 * Verify MFA code during enrollment
 * @param {string} method - 'email' or 'authenticator'
 * @param {string} code - 6-digit verification code
 * @returns {Promise<object>} - { success, verified, message }
 */
export async function verifyMFA(method, code) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/user-mfa-verify`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ method, code }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(errorData.error || `Failed to verify MFA code (${response.status})`);
            error.statusCode = response.status;
            throw error;
        }

        return await response.json();
    } catch (error) {
        console.error('Error verifying MFA:', error);
        throw error;
    }
}

/**
 * Disable MFA method
 * @param {string} method - 'email', 'authenticator', 'softwareOath', or 'microsoftAuthenticator'
 * @param {string} methodId - Optional method ID to disable
 * @returns {Promise<object>} - { success, message }
 */
export async function disableMFA(method, methodId = null) {
    try {
        const headers = await getHeaders();

        const body = { method };
        if (methodId) {
            body.methodId = methodId;
        }

        const response = await fetch(`${API_BASE_URL}/user-mfa-disable`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(errorData.error || `Failed to disable MFA (${response.status})`);
            error.statusCode = response.status;
            throw error;
        }

        return await response.json();
    } catch (error) {
        console.error('Error disabling MFA:', error);
        throw error;
    }
}

// Default export for convenience
const mfaService = {
    getMFAStatus,
    enableMFA,
    verifyMFA,
    disableMFA,
};

export default mfaService;
